import React, { memo, useMemo, useState } from "react";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { BankIcon } from "@icons";
import { PickerEntityItem } from "@components/entity/PickerEntityItem";
import {
    buildAccounts,
    buildEntities,
    buildFlatItems,
    buildSections,
    plainFlatItemOptions as plainOptions,
} from "./PickerPlayground.lib";
import { buildTreeItems } from "./PickerPlaygroundTree";
import { PickerButtonTriggerGrid } from "./PickerPlaygroundMatrixButtons";
import { PickerCellGrid } from "./PickerPlaygroundMatrixCell";
import { PickerFlyoutGrid } from "./PickerPlaygroundMatrixFlyout";
import {
    PickerLargeListGrid,
    PickerObjectRowsGrid,
} from "./PickerPlaygroundMatrixObjects";
import { PickerRootsGrid } from "./PickerPlaygroundMatrixRoots";
import { PickerSectionedList } from "./PickerPlaygroundSectionedList";
import { VariantGrid, VariantItem } from "./playgroundControls";
import styles from "./PickerPlayground.module.scss";

const decoratedOptions = {
    hasDisabledItems: true,
    hasDestructiveItem: true,
    hasDescriptions: false,
    hasRightText: true,
};

const withDescriptions = { ...plainOptions, hasDescriptions: true };

/**
 * Every toggle the sandbox covers, rendered in place through the root's
 * `inline` mode so the whole matrix can be screenshotted against the Figma
 * compositions frame (3831:9252) without opening a single popup.
 */
const InlineCell = ({
    isMultiple,
    items,
    defaultValue,
    searchable = false,
    children,
}: {
    isMultiple: boolean;
    items?: Array<PickerItemData<string>>;
    defaultValue?: string[];
    searchable?: boolean;
    children?: React.ReactNode;
}) => (
    <div className={styles.matrixCell}>
        <Picker.Select<string, boolean>
            inline
            searchable={searchable}
            items={items}
            {...(isMultiple
                ? { multiple: true, defaultValue }
                : { defaultValue: defaultValue?.[0] ?? null })}
        >
            {children}
        </Picker.Select>
    </div>
);

const ModeColumn = ({ isMultiple }: { isMultiple: boolean }) => {
    const plain = useMemo(() => buildFlatItems(plainOptions), []);
    const decorated = useMemo(() => buildFlatItems(decoratedOptions), []);
    const described = useMemo(() => buildFlatItems(withDescriptions), []);
    const sections = useMemo(() => buildSections(plainOptions), []);
    const tree = useMemo(
        () => buildTreeItems({ hasDescriptions: false, hasRightText: false }),
        [],
    );
    const mode = isMultiple ? "multiple" : "single";
    const selected = isMultiple
        ? [plain[0].value, plain[1].value]
        : [plain[1].value];
    const sectionItems = sections.flatMap((section) => section.items);
    const selectedInSections = isMultiple
        ? [sectionItems[0].value, sectionItems[1].value]
        : [sectionItems[1].value];

    return (
        <>
            <VariantItem label={`${mode} · plain`}>
                <InlineCell
                    isMultiple={isMultiple}
                    items={plain}
                    defaultValue={selected}
                />
            </VariantItem>
            <VariantItem label={`${mode} · searchable`}>
                <InlineCell
                    isMultiple={isMultiple}
                    items={plain}
                    searchable
                    defaultValue={selected}
                />
            </VariantItem>
            <VariantItem label={`${mode} · sections`}>
                <InlineCell
                    isMultiple={isMultiple}
                    defaultValue={selectedInSections}
                >
                    <PickerSectionedList
                        sections={sections}
                        query=""
                        hasFooter={false}
                        onFooterSelect={() => undefined}
                    />
                </InlineCell>
            </VariantItem>
            <VariantItem label={`${mode} · footer`}>
                <InlineCell
                    isMultiple={isMultiple}
                    defaultValue={selectedInSections}
                >
                    <PickerSectionedList
                        sections={sections.slice(0, 1)}
                        query=""
                        hasFooter
                        onFooterSelect={() => undefined}
                    />
                </InlineCell>
            </VariantItem>
            <VariantItem label={`${mode} · tree`}>
                <InlineCell
                    isMultiple={isMultiple}
                    items={tree}
                    defaultValue={[
                        tree[0].children?.[1]?.children?.[1]?.children?.[0]
                            ?.value ?? "",
                    ]}
                />
            </VariantItem>
            <VariantItem label={`${mode} · sections prop, nested select-all`}>
                <div className={styles.matrixCell}>
                    <Picker.Select<string, boolean>
                        inline
                        sections={[
                            {
                                title: "Chart of accounts",
                                items: tree,
                                selectAll: true,
                            },
                            {
                                title: "Bank accounts",
                                items: plain.slice(0, 2),
                                selectAll: true,
                            },
                        ]}
                        {...(isMultiple
                            ? {
                                  multiple: true,
                                  defaultValue: [
                                      tree[0].children?.[0]?.value ?? "",
                                      plain[0].value,
                                  ],
                              }
                            : { defaultValue: plain[0].value })}
                    />
                </div>
            </VariantItem>
            <VariantItem label={`${mode} · empty`}>
                <InlineCell isMultiple={isMultiple} items={[]} searchable />
            </VariantItem>
            <VariantItem label={`${mode} · disabled, destructive, right text`}>
                <InlineCell
                    isMultiple={isMultiple}
                    items={decorated}
                    defaultValue={selected}
                />
            </VariantItem>
            <VariantItem label={`${mode} · descriptions`}>
                <InlineCell isMultiple={isMultiple} items={described} />
            </VariantItem>
        </>
    );
};

const PresetsRow = () => {
    const entities = useMemo(() => buildEntities(), []);
    const accounts = useMemo(() => buildAccounts(), []);
    const [expandedEntity, setExpandedEntity] = useState<number | null>(null);
    const [toggles, setToggles] = useState({ pending: true, transfers: false });

    return (
        <VariantGrid title="Presets" columns="wide">
            <VariantItem label="PickerEntityItem">
                <div className={styles.matrixCell}>
                    <Picker.Select inline>
                        <Picker.List>
                            {entities.map((entity, index) => (
                                <PickerEntityItem
                                    key={entity.id}
                                    value={entity.id}
                                    entity={entity}
                                    expandable={index < 2}
                                    expanded={expandedEntity === entity.id}
                                    onExpandedChange={(expanded) =>
                                        setExpandedEntity(
                                            expanded ? entity.id : null,
                                        )
                                    }
                                />
                            ))}
                        </Picker.List>
                    </Picker.Select>
                </div>
            </VariantItem>
            <VariantItem label="Picker.AccountItem">
                <div className={styles.matrixCell}>
                    <Picker.Select inline defaultValue={accounts[0].value}>
                        <Picker.List>
                            {accounts.map((account) => (
                                <Picker.AccountItem
                                    key={account.value}
                                    value={account.value}
                                    name={account.name}
                                    mask={account.mask}
                                    icon={<BankIcon />}
                                />
                            ))}
                        </Picker.List>
                    </Picker.Select>
                </div>
            </VariantItem>
            <VariantItem label="Picker.ToggleItem">
                <div className={styles.matrixCell}>
                    <Picker.Select inline>
                        <Picker.List>
                            <Picker.ToggleItem
                                checked={toggles.pending}
                                onCheckedChange={(pending) =>
                                    setToggles((previous) => ({
                                        ...previous,
                                        pending,
                                    }))
                                }
                            >
                                Show pending
                            </Picker.ToggleItem>
                            <Picker.ToggleItem
                                checked={toggles.transfers}
                                onCheckedChange={(transfers) =>
                                    setToggles((previous) => ({
                                        ...previous,
                                        transfers,
                                    }))
                                }
                            >
                                Include transfers
                            </Picker.ToggleItem>
                        </Picker.List>
                    </Picker.Select>
                </div>
            </VariantItem>
        </VariantGrid>
    );
};

/**
 * Static combination matrix plus the item presets row. Memoised: it takes no
 * props, so a pick on the stage above must not re-render every cell here.
 */
export const PickerMatrix = memo(() => (
    <>
        <VariantGrid title="Single" columns="wide">
            <ModeColumn isMultiple={false} />
        </VariantGrid>
        <VariantGrid title="Multiple" columns="wide">
            <ModeColumn isMultiple />
        </VariantGrid>
        <PickerRootsGrid />
        <PickerObjectRowsGrid />
        <PickerLargeListGrid />
        <PickerButtonTriggerGrid />
        <PickerFlyoutGrid />
        <PickerCellGrid />
        <PresetsRow />
    </>
));
PickerMatrix.displayName = "PickerMatrix";
